import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';

export interface CarouselSlide {
  src: string;
  alt: string;
  caption?: string;
}

/**
 * Crossfading photo carousel ported from docs/2026c-redesign.html (.carousel / .personal-photo).
 * Auto-advances, pauses on hover, resumes after manual navigation, respects prefers-reduced-motion.
 */
@Component({
  selector: 'app-photo-carousel-v2026',
  imports: [CommonModule],
  templateUrl: './photo-carousel-v2026.component.html',
  styleUrl: './photo-carousel-v2026.component.scss'
})
export class PhotoCarouselV2026Component implements OnInit, OnDestroy {
  @Input() slides: CarouselSlide[] = [];
  /** Auto-advance interval in ms. 0 disables auto-advance. */
  @Input() intervalMs = 5000;
  /** Delay before auto-advance resumes after the user clicks prev/next. */
  @Input() resumeMs = 12000;
  @Input() aspectRatio = '16 / 9';
  /** First slide loads eagerly when the carousel is above the fold. */
  @Input() eager = false;

  index = signal(0);
  fading = signal(false);

  private timer: ReturnType<typeof setInterval> | null = null;
  private resumeTimer: ReturnType<typeof setTimeout> | null = null;
  private reducedMotion = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  get current(): CarouselSlide | undefined {
    return this.slides[this.index()];
  }

  get hasMany(): boolean {
    return this.slides.length > 1;
  }

  get hasCaptions(): boolean {
    return this.slides.some(s => !!s.caption);
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.slides.forEach(s => { const img = new Image(); img.src = s.src; });
    if (!this.reducedMotion) this.startAuto();
  }

  ngOnDestroy(): void {
    this.stopAuto();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
  }

  show(n: number): void {
    if (!this.hasMany) return;
    const next = (n + this.slides.length) % this.slides.length;
    if (this.reducedMotion) { this.index.set(next); return; }
    this.fading.set(true);
    setTimeout(() => { this.index.set(next); this.fading.set(false); }, 180);
  }

  manual(dir: 1 | -1): void {
    this.show(this.index() + dir);
    this.stopAuto();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    if (!this.reducedMotion) this.resumeTimer = setTimeout(() => this.startAuto(), this.resumeMs);
  }

  pause(): void { this.stopAuto(); }

  resume(): void {
    if (this.resumeTimer) { clearTimeout(this.resumeTimer); this.resumeTimer = null; }
    if (!this.reducedMotion) this.startAuto();
  }

  private startAuto(): void {
    this.stopAuto();
    if (this.intervalMs > 0 && this.hasMany) {
      this.timer = setInterval(() => this.show(this.index() + 1), this.intervalMs);
    }
  }

  private stopAuto(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }
}
